import React, { useEffect, useState } from 'react';
import { Accessibility, Check, X, AlertTriangle, Copy, ExternalLink } from 'lucide-react';
import api from '../lib/api';

/**
 * Admin helper to bulk-paste direct AccessAble URLs.
 *
 * Why this exists: the Google site-search auto-linker has spotty coverage —
 * AccessAble's slugs aren't deterministically derivable from venue names.
 * Letting the admin paste a batch of `venue-slug -> URL` pairs in one go is
 * far faster than editing every venue individually.
 *
 * UX:
 *   - Top half: candidate list (venues without a direct URL). Slugs are
 *     click-to-copy so the admin can paste them straight into the textarea.
 *   - Bottom half: textarea + submit. Lines accept several separators:
 *       slug, url      slug url      slug -> url      slug | url
 *   - After submit: per-line outcome (updated / not_found / invalid) so the
 *     admin can spot typos without re-pasting the whole list.
 */
export default function AccessableBulkLink() {
  const [open, setOpen] = useState(false);
  const [candidates, setCandidates] = useState(null);
  const [pairs, setPairs] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [copiedSlug, setCopiedSlug] = useState(null);

  useEffect(() => {
    if (!open || candidates !== null) return;
    api.get('/admin/accessibility/accessable-candidates')
      .then((r) => setCandidates(r.data.items || []))
      .catch(() => setCandidates([]));
  }, [open, candidates]);

  async function submit() {
    if (!pairs.trim() || submitting) return;
    setSubmitting(true);
    setResult(null);
    try {
      const r = await api.post('/admin/accessibility/bulk-link-accessable', { pairs });
      setResult(r.data);
      // Refresh candidates so updated venues disappear from the list
      const c = await api.get('/admin/accessibility/accessable-candidates');
      setCandidates(c.data.items || []);
      // Clear textarea on full success
      if (r.data.not_found === 0 && r.data.invalid === 0) setPairs('');
    } catch (e) {
      setResult({ error: e.response?.data?.detail || e.message || 'Bulk-link failed' });
    } finally {
      setSubmitting(false);
    }
  }

  function copySlug(slug) {
    navigator.clipboard?.writeText(slug);
    setCopiedSlug(slug);
    setTimeout(() => setCopiedSlug((c) => (c === slug ? null : c)), 1200);
  }

  return (
    <div className="w-full" data-testid="accessable-bulk-link-tile">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="toggle-accessable-bulk"
        className="w-full text-left block bg-admin border border-charcoal/10 px-5 py-4 rounded-xl font-semibold hover:border-charcoal/30 transition-colors"
      >
        <span className="inline-flex items-center gap-2">
          <Accessibility size={16} />
          {open ? 'Hide AccessAble bulk-paste helper' : 'Paste AccessAble guide URLs in bulk →'}
        </span>
        <span className="block text-xs text-muted mt-1 font-normal">
          The auto-linker uses Google site-search which is patchy. Open AccessAble in another tab,
          search each venue, copy the direct URL, then paste them all here in one go.
        </span>
      </button>

      {open && (
        <div className="mt-3 bg-admin border border-charcoal/10 rounded-xl p-5 space-y-5" data-testid="accessable-bulk-panel">
          {/* Candidates list ------------------------------------------------ */}
          <div>
            <div className="flex items-baseline justify-between mb-2">
              <div className="font-semibold text-sm">
                Venues still needing a direct URL
                {candidates && <span className="text-muted font-normal"> · {candidates.length}</span>}
              </div>
              <a
                href="https://www.accessable.co.uk/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-forest inline-flex items-center gap-1 hover:underline"
              >
                Open AccessAble <ExternalLink size={11} />
              </a>
            </div>
            <div className="max-h-56 overflow-y-auto border border-charcoal/10 rounded-lg bg-stone/30 divide-y divide-charcoal/5">
              {candidates === null && <div className="text-xs text-muted px-3 py-2">Loading…</div>}
              {candidates && candidates.length === 0 && (
                <div className="text-xs text-muted px-3 py-3">
                  Nothing left — every published venue has a direct AccessAble URL or has been verified.
                </div>
              )}
              {candidates && candidates.map((c) => (
                <div key={c.slug} className="px-3 py-2 flex items-center justify-between gap-3 text-xs">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-charcoal truncate">{c.name}</div>
                    <div className="text-muted truncate">
                      <code className="text-[10px]">{c.slug}</code>
                      {c.town && <span> · {c.town}</span>}
                      {c.has_auto_link && <span className="text-amber-700"> · auto-link only</span>}
                    </div>
                  </div>
                  <button
                    onClick={() => copySlug(c.slug)}
                    data-testid={`copy-slug-${c.slug}`}
                    className="shrink-0 inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-charcoal/15 hover:border-charcoal/40"
                  >
                    {copiedSlug === c.slug ? <Check size={11} /> : <Copy size={11} />}
                    {copiedSlug === c.slug ? 'Copied' : 'Copy slug'}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Paste form ----------------------------------------------------- */}
          <div>
            <label className="block text-sm font-semibold mb-1" htmlFor="accessable-pairs">
              Paste AccessAble URLs (one per line)
            </label>
            <p className="text-xs text-muted mb-2">
              Just paste the URLs — we&apos;ll auto-match each one to a venue using the last
              segment of the URL. If a URL won&apos;t match, you can also paste it as
              <code> slug, url</code> (or <code>slug -&gt; url</code>) to force the pairing.
              Lines starting with <code>#</code> are ignored.
            </p>
            <textarea
              id="accessable-pairs"
              data-testid="accessable-pairs-input"
              value={pairs}
              onChange={(e) => setPairs(e.target.value)}
              rows={8}
              spellCheck={false}
              placeholder={`https://www.accessable.co.uk/leicester-and-leicestershire/access-guides/bradgate-park\nhttps://www.accessable.co.uk/leicester-and-leicestershire/access-guides/twycross-zoo`}
              className="w-full font-mono text-xs bg-white border border-charcoal/15 rounded-lg px-3 py-2 focus:border-forest focus:outline-none"
            />
            <div className="flex items-center gap-3 mt-2">
              <button
                onClick={submit}
                disabled={submitting || !pairs.trim()}
                data-testid="accessable-bulk-submit"
                className="px-4 py-2 rounded-lg bg-charcoal text-white text-sm font-semibold hover:bg-charcoal/85 disabled:opacity-50"
              >
                {submitting ? 'Applying…' : 'Apply URLs'}
              </button>
              {pairs && (
                <button
                  onClick={() => { setPairs(''); setResult(null); }}
                  className="text-xs text-muted hover:text-charcoal"
                  data-testid="accessable-bulk-clear"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Result --------------------------------------------------------- */}
          {result && (
            <div className="border border-charcoal/10 rounded-lg p-3 bg-stone/30" data-testid="accessable-bulk-result">
              {result.error ? (
                <div className="text-sm text-red-700 inline-flex items-center gap-2">
                  <AlertTriangle size={14} /> {result.error}
                </div>
              ) : (
                <>
                  <div className="text-sm font-semibold mb-2">
                    <span className="text-forest">{result.updated} updated</span>
                    {result.not_found ? <span className="text-amber-700"> · {result.not_found} not found</span> : null}
                    {result.invalid ? <span className="text-red-700"> · {result.invalid} invalid</span> : null}
                  </div>
                  <div className="max-h-40 overflow-y-auto text-xs space-y-1">
                    {(result.results || []).map((r) => (
                      <div key={r.line} className="flex items-start gap-2">
                        {r.status === 'updated' && <Check size={12} className="text-forest mt-0.5 shrink-0" />}
                        {r.status === 'not_found' && <AlertTriangle size={12} className="text-amber-700 mt-0.5 shrink-0" />}
                        {r.status === 'invalid' && <X size={12} className="text-red-700 mt-0.5 shrink-0" />}
                        <div className="min-w-0 flex-1">
                          <span className="text-muted">L{r.line}</span>{' '}
                          {r.status === 'updated' && <span><strong>{r.name}</strong> ({r.slug})</span>}
                          {r.status === 'not_found' && (
                            <span>
                              No venue matched <code>{r.slug}</code>
                              {r.hint && <span className="block text-[10px] text-muted">{r.hint}</span>}
                            </span>
                          )}
                          {r.status === 'invalid' && <span>Couldn&apos;t parse: <code>{r.raw}</code></span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
